// @ts-check
// ── The Clients card: the client goal, joined and left each month, who stayed ──
//
// Part 5 of docs/superpowers/plans/2026-10-14-dashboard-build.md, from the approved prototype
// (mockups/ultimate-dashboard-v3-2026-10-13.html: goalStrip, joinLeftChart, clientStats, countVis) and Clayton's 10/13
// rulings (the goal strip at the top; forward-looking on purpose, no grade on past history). The server
// (_shared/dashboard/client-flow.js) works out every number; this only says them.
//
// ⚠️ THE GOAL IS THE COACH'S CLIENT CAPACITY (coaches.settings.maxClientCapacity): Settings saves the same number
// and the phone reads it. Saved through save-coach-settings like Settings' box, and into the browser's copy too, so
// Settings shows the same number. The goal is never kept in the address (the prototype's &goal=).

import { callFunction } from '../api.js';
import { loadSettings, saveSettingsToStorage } from '../config.js';
import { escHtml } from '../utils.js';
import { plural, monthOf, longDay } from './words.js';
import { info, status } from './tiles.js';
import { tipRows, table } from './charts.js';
import { renewalsCardHtml } from './renewals.js';

const has = (/** @type {unknown} */ v) => v != null;
/** 'Oct' from '2026-10' @param {string} ym */
const short = (ym) => monthOf(`${ym}-01`).slice(0, 3);
/** 'October' from '2026-10' @param {string} ym */
const full = (ym) => monthOf(`${ym}-01`);
const pct = (/** @type {number} */ a, /** @type {number} */ b) => Math.round((a / b) * 100);

/** The goal being changed, kept across redraws (a client change redraws the page). */
const goalEdit = { open: false, value: '', error: '', saving: false };
/** The Clients answer last drawn, so the goal's panel redraws on its own. @type {any} */
let lastC = null;
let lastYear = 0;
/** The goal panel asked for the keyboard (Change, a wrong number, a failed save): the box takes it and selects. */
let wantFocus = false;
/** The box had the keyboard when the whole page redrew (a client change): it keeps it, and its caret. @type {{ s: number|null, e: number|null }|null} */
let keepFocus = null;
/** The row on the page now: a redraw during a save replaces the one the form was bound to (final review 10/16). @param {ParentNode} root */
const liveRow = (root) => document.querySelector('#dv-root .dv-clients-row') || root;

/**
 * How many of how many, as a picture: nothing under 3 ("0 of 1" says it), a dot each up to 19, a bar from 20.
 * @param {number} a @param {number} b
 */
export function countVis(a, b) {
  if (b < 3) return '';
  if (b < 20) return `<div class="dv-dots" role="img" aria-label="${a} of ${b}">${Array.from({ length: b }, (_, i) => `<i${i < a ? '' : ' class="o"'}></i>`).join('')}</div>`;
  return `<div class="dv-bar" role="img" aria-label="${a} of ${b}"><i style="width:${(a / b) * 100}%"></i></div>`;
}

/** One stacked figure, with what it waits for when it has no number yet. */
function stat(/** @type {string} */ label, /** @type {number|null} */ num, /** @type {string} */ of, /** @type {string} */ sub, /** @type {string} */ vis) {
  return `<div class="dv-stat">
      <span class="dv-label">${label}</span>
      <div class="dv-value">${num == null ? status('none', 'Not enough history yet') : `<span class="dv-num">${num}</span>${of ? `<span class="dv-of">${of}</span>` : ''}`}</div>
      ${vis ? `<div class="dv-vis">${vis}</div>` : ''}
      ${sub ? `<div class="dv-sub">${sub}</div>` : ''}
    </div>`;
}

/** The goal's first box: the goal, how far, and its Change button; or the form to change it. @param {any} C */
function goalCell(C) {
  const goal = C.goal, now = C.now;
  if (goalEdit.open) {
    return `<div class="dv-goal-c">
        <div class="dv-goal-h"><span class="dv-label">Your goal</span></div>
        <form class="dv-goal-form" novalidate>
          <label class="dv-sr" for="dv-goal-in">Your client goal</label>
          <input id="dv-goal-in" class="dv-goal-in" type="text" inputmode="numeric" pattern="[0-9]*" maxlength="3" autocomplete="off" value="${escHtml(goalEdit.value)}"${goalEdit.error ? ' aria-invalid="true" aria-describedby="dv-goal-err"' : ''}>
          <span class="dv-of">clients</span>
          <button type="submit" class="btn-primary"${goalEdit.saving ? ' disabled' : ''}>${goalEdit.saving ? 'Saving…' : 'Save'}</button>
          <button type="button" class="btn-secondary" data-goal-cancel${goalEdit.saving ? ' disabled' : ''}>Cancel</button>
        </form>
        ${goalEdit.error ? `<div class="dv-sub dv-goal-err" id="dv-goal-err" role="alert">${escHtml(goalEdit.error)}</div>` : ''}
      </div>`;
  }
  if (!goal) {
    return `<div class="dv-goal-c">
        <div class="dv-goal-h"><span class="dv-label">Your goal</span><button type="button" class="dv-link dv-goal-edit">Set your goal</button></div>
        <div class="dv-value"><span class="dv-num">${now}</span><span class="dv-of">${now === 1 ? 'client' : 'clients'} now</span></div>
        <div class="dv-sub">Set a goal to see what it takes to reach it</div>
      </div>`;
  }
  const reached = now >= goal;
  return `<div class="dv-goal-c">
      <div class="dv-goal-h"><span class="dv-label">Your goal</span><button type="button" class="dv-link dv-goal-edit" aria-label="Change your goal of ${goal} clients">Change</button></div>
      <div class="dv-value"><span class="dv-num">${now}</span><span class="dv-of">of ${plural(goal, 'client', 'clients')}</span></div>
      <div class="dv-bar" role="img" aria-label="${now} of ${goal} clients"><i style="width:${Math.min(100, (now / goal) * 100)}%"></i></div>
      <div class="dv-sub">${reached ? (now === goal ? 'You are at your goal' : `${plural(now - goal, 'client', 'clients')} past your goal`) : `${plural(goal - now, 'client', 'clients')} to go`}</div>
    </div>`;
}

/** The goal's second box: new clients a month to reach it in a year, or to stay there. @param {any} C @param {number} year */
function paceCell(C, year) {
  const P = C.pace;
  const label = !C.goal || !P.reached ? 'To reach it in a year' : 'To stay at your goal';
  const months = P.months === 1 ? 'month' : `${P.months} months`;
  const a = (/** @type {number} */ n) => `<span class="dv-num">${n}</span><span class="dv-of">new ${n === 1 ? 'client' : 'clients'} a month</span>`;
  let body;
  if (!C.goal) body = `<div class="dv-value">${status('none', 'Fills in once you set your goal')}</div>`;
  // ⚠️ Never the pace of another goal: a saved goal whose numbers have not come back yet waits (final review 10/16)
  else if (P.goal !== C.goal) body = `<div class="dv-value">${status('none', 'Refresh the page to see what it takes')}</div><div class="dv-sub">Your new goal is saved.</div>`;
  else if (!P.enough) body = `<div class="dv-value">${status('none', P.fillsOn ? `Fills in on ${longDay(P.fillsOn, year)}` : 'Not enough history yet')}</div><div class="dv-sub">It needs 3 full months to see how many clients leave each month.</div>`;
  else if (P.nobodyLeft) {
    body = P.need != null
      ? `<div class="dv-value">${a(P.need)}</div><div class="dv-sub">Nobody left in the last ${months}, so every new client adds to your count.</div>`
      : `<div class="dv-value"><span class="dv-of dv-of--words">Nobody left in the last ${months}</span></div><div class="dv-sub">Every new client adds to your count.</div>`;
  } else {
    body = `<div class="dv-value">${a(P.need)}</div><div class="dv-sub">${P.reached ? '' : `Then about ${P.hold} a month keeps you there. `}About 1 in ${P.oneIn} of your clients leaves each month, over the last ${months}.</div>`;
  }
  return `<div class="dv-goal-c"><span class="dv-label">${label}</span>${body}</div>`;
}

/** The goal's third box: this month so far, against the number a month it takes. @param {any} C */
function monthCell(C) {
  const T = C.thisMonth, need = C.goal && C.pace.goal === C.goal && C.pace.enough ? C.pace.need : null;
  const signed = T.joined + T.returned, gone = T.left + T.paused;
  return `<div class="dv-goal-c">
      <span class="dv-label">This month so far</span>
      <div class="dv-value"><span class="dv-num">${signed}</span><span class="dv-of">${need ? `of ${need} signed` : 'signed'}</span></div>
      ${need ? countVis(Math.min(signed, need), need) : ''}
      <div class="dv-sub">${gone ? `${plural(gone, 'client has', 'clients have')} ${T.paused ? 'left or paused' : 'left'} this month` : 'Nobody has left this month'}</div>
    </div>`;
}

/** The goal panel: the goal, the pace to it, this month so far. @param {any} C @param {number} year */
const goalStrip = (C, year) => `<div class="dv-goal" role="group" aria-label="Client goal">${goalCell(C)}${paceCell(C, year)}${monthCell(C)}</div>`;

/**
 * The row: Clients (8 of 12 columns) and Renewals (4).
 * @param {any} d the server's answer
 */
export function clientsRowHtml(d) {
  const C = d.clients, year = Number(String(d.today || '').slice(0, 4));
  lastC = C; lastYear = year;
  // Drawn into a string while the old row is still on the page: if the box has the keyboard, the new box keeps it
  const el = /** @type {HTMLInputElement|null} */ (document.activeElement);
  keepFocus = el && el.id === 'dv-goal-in' ? { s: el.selectionStart, e: el.selectionEnd } : null;
  const S = C.stay90, Y = C.stayYear, R = C.renewed;
  const since = (/** @type {string|null} */ day) => (day ? ` since ${longDay(day, year)}` : '');
  const stats = [
    stat('Still here after 90 days', S.of ? S.kept : null, `of ${S.of}`,
      S.of ? `${S.of >= 20 ? `<b>${pct(S.kept, S.of)}%</b> of new clients` : 'New clients'}${since(S.since)} still with you 90 days after joining` : 'Fills in 90 days after your first new clients join',
      S.of ? countVis(S.kept, S.of) : ''),
    stat('Still here after a year', Y.of ? Y.kept : null, `of ${Y.of}`,
      Y.of ? (Y.of >= 20 ? `<b>${pct(Y.kept, Y.of)}%</b> of the clients you had a year ago` : 'Of the clients you had a year ago') : Y.fillsOn ? `Fills in on ${longDay(Y.fillsOn, year)}` : 'Fills in after your first year',
      Y.of ? countVis(Y.kept, Y.of) : ''),
    stat('Programs renewed', R.of ? R.renewed : null, `of ${R.of}`,
      R.of ? `${R.of >= 20 ? `<b>${pct(R.renewed, R.of)}%</b> of the programs` : 'Programs'} that ended ${R.since ? `since ${longDay(R.since, year)}` : 'in the last 6 months'} were renewed` : 'Fills in when your first programs end',
      R.of ? countVis(R.renewed, R.of) : ''),
  ];
  const clientsCard = `<section class="dv-card dv-s8" aria-labelledby="dv-cl-h">
      <div class="dv-card-head"><div class="dv-head-l"><h2 class="dv-title" id="dv-cl-h">Clients</h2>${info('clients')}</div></div>
      ${goalStrip(C, year)}
      <div class="dv-split-card">
        <div class="dv-jl-col"><div class="dv-jl-h"><span class="dv-label">Joined and left each month</span></div>
          <div class="dv-plotbox dv-fill" data-chart="jl"></div>
          <div class="dv-chart-note">Joined above the line, left below it.</div></div>
        <div class="dv-stats">${stats.join('')}</div>
      </div>
    </section>`;
  return `<div class="dv-grid dv-clients-row">${clientsCard}${renewalsCardHtml(d.renewals)}</div>`;
}

/**
 * Joined (and came back) above the line, left (and paused) below it, each month; as tall as the figures beside it.
 * @param {Array<{ month: string, joined: number, returned: number, left: number, paused: number }|null>} byMonth
 * @param {Array<{ month: string }>} months the 12 months, for their names @param {number} W @param {number} HB the box's free height
 */
export function joinLeftChart(byMonth, months, W, HB) {
  const HH = Math.round(Math.max(200, Math.min(340, HB - 26))), mid = Math.round(HH / 2), Lc = 44;
  const up = byMonth.map((f) => (f ? f.joined + f.returned : null)), down = byMonth.map((f) => (f ? f.left + f.paused : null));
  const max = Math.max(1, .../** @type {number[]} */ (up.filter(has)), .../** @type {number[]} */ (down.filter(has)));
  const slot = (W - Lc) / byMonth.length, bw = Math.min(16, slot * 0.5), last = byMonth.length - 1;
  const sc = (/** @type {number} */ n) => (n / max) * (mid - 22);
  const peakOf = (/** @type {Array<number|null>} */ arr) => { let pi = -1; arr.forEach((n, i) => { if (has(n) && (pi < 0 || /** @type {number} */ (n) > /** @type {number} */ (arr[pi]))) pi = i; }); return pi; };
  const pj = peakOf(up), pl = peakOf(down);
  const real = up.filter(has).length;
  const sum = (/** @type {Array<number|null>} */ a) => a.reduce((t, n) => /** @type {number} */ (t) + (n || 0), 0);
  let s = `<svg viewBox="0 0 ${W} ${HH + 22}" class="dv-plot" tabindex="0" role="img" aria-label="Clients joined and left by month. ${real < 12 ? `Last ${plural(real, 'month', 'months')}` : 'Last 12 months'}: ${sum(up)} joined, ${sum(down)} left.">`;
  s += `<line class="base" x1="${Lc}" x2="${W}" y1="${mid}" y2="${mid}"/><text x="0" y="${mid - 8}">Joined</text><text x="0" y="${mid + 18}">Left</text>`;
  byMonth.forEach((f, i) => {
    const x = Lc + slot * i + (slot - bw) / 2;
    if (slot >= 34 || i % 2 === last % 2) s += `<text x="${x + bw / 2}" y="${HH + 16}" text-anchor="middle">${short(months[i].month)}</text>`;
    if (!f) return;
    const j = /** @type {number} */ (up[i]), l = /** @type {number} */ (down[i]), hj = sc(j), hl = sc(l);
    /** @type {Array<[string, string, string]>} */
    const rows = [['Joined', String(f.joined), 'bar'], ...(/** @type {Array<[string, string, string]>} */ (f.returned ? [['Came back', String(f.returned), 'bar']] : [])),
      ['Left', String(f.left), 'left'], ...(/** @type {Array<[string, string, string]>} */ (f.paused ? [['Paused', String(f.paused), 'left']] : []))];
    s += `<g class="col" data-i="${i}" ${tipRows(`${full(f.month)}${i === last ? ' so far' : ''}`, rows, null)}>`;
    if (hj > 0) s += `<path class="bar" d="M${x},${mid - 1} V${mid - 1 - hj + 3} Q${x},${mid - 1 - hj} ${x + 3},${mid - 1 - hj} H${x + bw - 3} Q${x + bw},${mid - 1 - hj} ${x + bw},${mid - 1 - hj + 3} V${mid - 1} Z"/>`;
    if (hl > 0) s += `<path class="bar-left" d="M${x},${mid + 1} V${mid + 1 + hl - 3} Q${x},${mid + 1 + hl} ${x + 3},${mid + 1 + hl} H${x + bw - 3} Q${x + bw},${mid + 1 + hl} ${x + bw},${mid + 1 + hl - 3} V${mid + 1} Z"/>`;
    s += `<rect class="hit" x="${Lc + slot * i}" y="0" width="${slot}" height="${HH}"/></g>`;
    // The counts printed: this month and the busiest month, on both sides of the line
    if (i === last || (i === pj && pj < last - 1)) s += `<text class="val" x="${x + bw / 2}" y="${mid - 1 - hj - 6}" text-anchor="middle">${j}</text>`;
    if (i === last || (i === pl && pl < last - 1)) s += `<text class="val" x="${x + bw / 2}" y="${mid + 1 + hl + 15}" text-anchor="middle">${l}</text>`;
  });
  return s + '</svg>' + table('Clients joined and left by month', byMonth.map((f, i) => [full(months[i].month), f ? `${up[i]} joined, ${down[i]} left` : 'Before FitOps had your client list']));
}

/**
 * The goal's Change button and form, and Open Renewals.
 * @param {ParentNode|null} root @param {() => Promise<boolean>} saved runs after a goal is saved: fetch the numbers
 *   again; true once the new answer is drawn
 */
export function bindClientsRow(root, saved) {
  if (!root) return;
  bindGoal(root, saved);
  root.querySelector('[data-open-renewals]')?.addEventListener('click', () => {
    /** @type {HTMLElement|null} */ (document.querySelector('.nav-btn[data-view="renewals"]'))?.click();
    /** @type {any} */ (window).setPipelineWindow?.(60); // the page shows the card's 60 days
  });
}

/** @param {ParentNode} root @param {() => Promise<boolean>} saved */
function bindGoal(root, saved) {
  const redraw = () => {
    const row = liveRow(root), panel = row.querySelector('.dv-goal');
    if (panel) panel.outerHTML = goalStrip(lastC, lastYear);
    bindGoal(row, saved);
  };
  const focusChange = () => /** @type {HTMLElement|null} */ (liveRow(root).querySelector('.dv-goal-edit'))?.focus();
  const close = () => {
    Object.assign(goalEdit, { open: false, value: '', error: '', saving: false });
    redraw();
    focusChange();
  };
  root.querySelector('.dv-goal-edit')?.addEventListener('click', () => {
    // The saved goal, else the capacity Settings kept in this browser (Clayton's 75 before 10/16), saved only on Save
    let start = lastC && lastC.goal;
    if (!start) { try { start = Number(loadSettings().maxClientCapacity) || null; } catch { start = null; } }
    Object.assign(goalEdit, { open: true, value: start ? String(start) : '', error: '', saving: false });
    wantFocus = true;
    redraw();
  });
  const input = /** @type {HTMLInputElement|null} */ (root.querySelector('#dv-goal-in'));
  if (!input) return;
  // ⚠️ The keyboard moves only when the goal panel asks, or stays where it was across a whole-page redraw: a client
  // change redrawing the page must never pull it into the box, or select what was typed (final review 10/16)
  if (wantFocus) { wantFocus = false; input.focus(); input.select(); }
  else if (keepFocus) { const k = keepFocus; keepFocus = null; input.focus(); if (k.s != null) input.setSelectionRange(k.s, k.e == null ? k.s : k.e); }
  input.addEventListener('input', () => { goalEdit.value = input.value; });
  input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } });
  root.querySelector('[data-goal-cancel]')?.addEventListener('click', close);
  root.querySelector('.dv-goal-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (goalEdit.saving) return;
    const raw = input.value.trim(), n = Number(raw);
    if (!/^\d+$/.test(raw) || n < 1 || n > 999) { Object.assign(goalEdit, { value: raw, error: 'Enter a whole number from 1 to 999.' }); wantFocus = true; redraw(); return; }
    Object.assign(goalEdit, { value: raw, error: '', saving: true });
    redraw();
    try {
      await callFunction('save-coach-settings', { settings: { maxClientCapacity: n } }, 'POST');
      // The browser's copy too: the next Set starts from it
      try { const s = loadSettings(); s.maxClientCapacity = n; saveSettingsToStorage(s); } catch { /* the server has it */ }
    } catch {
      Object.assign(goalEdit, { error: 'Your goal was not saved. Try again.', saving: false });
      wantFocus = true;
      redraw();
      return;
    }
    // ⚠️ Saving… stays until the dashboard's new answer draws: its pace is worked out for the new goal (final review
    // 10/16: the strip showed the new goal beside the old goal's pace). The form closes in that drawing.
    Object.assign(goalEdit, { open: false, value: '', error: '', saving: false });
    if (!(await saved())) {
      // Nothing new was drawn: the same goal again (lastC is right), or the numbers did not come back (the pace waits)
      if (!lastC || lastC.goal !== n) lastC = { ...lastC, goal: n };
      redraw();
    }
    focusChange();
  });
}
